import {AfterViewChecked, Component, ElementRef, Input, OnInit, Optional, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivityService} from '../../../../../services/crm/activity/activity.service';
import {Activity} from '../../../../../models/crm/activity';
import {UserService} from '../../../../../services/user/user.service';
import {User} from '../../../../../models/user';
import { DatePipe, NgClass, NgOptimizedImage } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Partner} from '../../../../../models/partner/partner';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {IsGranted} from '../../../../../decorator/is-granted';
import {PartnerTabComponent} from '../../partner-tab/partner-tab.component';
import {Paginator} from '../../../../../tools/pagination/paginator';
import {AuthService} from '../../../../../services/auth/auth.service';
import {ActivityCommentService} from '../../../../../services/crm/activity/activity-comment.service';
import {ActivityComment} from '../../../../../models/crm/activity-comment';
import {Observable} from 'rxjs';
import {ImageService} from '../../../../../services/image/image.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {PartnerActivityFormComponent} from '../../partner-activity-form/partner-activity-form.component';
import {AnnualPerformanceComponent} from '../annual-performance/annual-performance.component';
import {HistoryPerformanceComponent} from '../history-performance/history-performance.component';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {ElementLoaderDirective} from '../../../../../directives/element-loader/element-loader.directive';
import {PartnerStat} from '../../../../../models/statistic/partner-stat';
import {StatisticsService} from '../../../../../services/statistics/statistics.service';
import {CappingRuleListComponent} from '../capping-rule/capping-rule-list/capping-rule-list.component';
import {BlockRuleListComponent} from '../block-rule/block-rule-list/block-rule-list.component';
import {ListFilters} from '../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../services/list-filter/list-filter.service';

const ACTIVITY_TYPES: { [group: string]: string[] } = {
  note: ['note'],
  task: ['task'],
  notification: [
    'new_partner',
    'prospect_conversion',
    'account_manager_update',
    'payment_terms_update',
    'partner_offer_request',
    'partner_account_update',
    'partner_activated',
    'partner_rejected',
  ],
  login: ['login'],
  signup: ['signup'],
};

@Component({
  selector: 'app-activity-feed',
  imports: [
    DatePipe,
    FormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgClass,
    NgOptimizedImage,
    AnnualPerformanceComponent,
    HistoryPerformanceComponent,
    ConfirmDirective,
    ElementLoaderDirective,
    CappingRuleListComponent,
    BlockRuleListComponent
],
  templateUrl: './activity-feed.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './activity-feed.component.scss'
})
@IsGranted(['ACTIVITY_VIEW'])
@MetaTitle('Activity Feed')
export class ActivityFeedComponent implements OnInit, AfterViewChecked {

  @ViewChild('observer', { static: false }) observerElement!: ElementRef;
  private observerAttached: boolean = false;

  connectedUser!: User;
  partner: Partner | null = null;

  activities: Activity[] = [];
  users: User[] = [];
  partnerStat: PartnerStat = new PartnerStat();

  paginator: Paginator = new Paginator(1);
  statsLoaded: boolean = false;
  loaded: boolean = false;
  activityDataLoading: boolean = false;
  submitting: boolean = false;

  filters: ListFilters;

  constructor(
    private imageService: ImageService,
    private auth: AuthService,
    private activityService: ActivityService,
    private activityCommentService: ActivityCommentService,
    private userService: UserService,
    private snackbar: SnackbarService,
    private dialog: DialogService,
    private statisticsService: StatisticsService,
    listFilterService: ListFilterService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
    this.filters = listFilterService.createFilters({
      senderId: 'number[]',
      type: 'string[]',
      global: 'boolean',
      feedDisplay: 'boolean',
    });
  }

  ngOnInit(): void {
    this.auth.getConnectedUser().subscribe((connectedUser: User) => {
      this.connectedUser = connectedUser;
    });

    this.userService.findAll({accountManager: true, sort: 'firstname,lastname'}).subscribe((users: User[]) => {
      this.users = users;
    });

    if (this.partnerTab) {
      this.partnerTab.partnerSubject.subscribe((partner: Partner | null) => {
        if (partner) {
          this.partner = partner;

          this.statisticsService.getActivity(partner).subscribe((partnerStat: PartnerStat) => {
            this.partnerStat = partnerStat;
            this.statsLoaded = true;
          });

          this.fetchActivities();
        }
      });
    } else {
      this.statisticsService.getActivity().subscribe((partnerStat: PartnerStat) => {
        this.partnerStat = partnerStat;
        this.statsLoaded = true;
      });

      this.filters.init(() => this.fetchActivities());
    }

    this.activityService.activitySubject.subscribe(() => {
      this.fetchActivities();
    });
  }

  ngAfterViewChecked(): void {
    if (!this.observerAttached && this.observerElement) {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && this.paginator.currentPage < this.paginator.pageCount) {
          ++this.paginator.currentPage;

          this.fetchActivities(true);
        }
      });

      observer.observe(this.observerElement.nativeElement);

      this.observerAttached = true;
    }
  }

  addAction(activity: Activity, action: string): void {
    if (activity.id) {
      this.activityDataLoading = true;

      const data = activity.data ?? {};
      data['action'] = action;

      this.activityService.update(activity.id, {data}).subscribe(() => {
        activity.data = data;

        this.activityDataLoading = false;
      });
    }
  }

  toggleFeedDisplay(): void {
    this.filters.values['feedDisplay'] = !this.filters.values['feedDisplay'];

    this.onFilterChange();
  }

  onFilterChange(): void {
    if (this.partner) {
      this.fetchActivities();
    } else {
      this.filters.onFilterChange();
    }
  }

  fetchActivities(loadMore: boolean = false): void {
    if (!loadMore) {
      this.paginator = new Paginator(25);
    }

    this.loaded = false;

    const parameters: any = {groups: 'image,comments', sort: 'pinned,updatedAt,createdAt', order: 'desc'};
    const values: any = {
      ...this.filters.values,
      type: this.filters.values['type'].flatMap((group: string) => ACTIVITY_TYPES[group] ?? []),
    };

    for (const [index, value] of Object.entries(values)) {
      if (value && (!Array.isArray(value) || value.length)) {
        parameters[index] = value;
      }
    }

    if (this.partner) {
      parameters['partnerId'] = this.partner.id;
    }

    this.activityService.findAll(parameters, this.paginator).subscribe((activities: Activity[]) => {
      if (loadMore) {
        for (const activity of activities) {
          this.activities.push(activity);
        }
      } else {
        this.activities = activities;
      }

      this.loaded = true;
    });
  }

  updateComments(activity: Activity): Observable<void> {
    return new Observable<void>((resolver) => {
      this.activityCommentService.findAll({activityId: activity.id, groups: 'image'}).subscribe((comments: ActivityComment[]) => {
        activity.deserialize({comments});

        resolver.next();
      });
    });
  }

  getImageUrl(person: User | Partner | null): string {
    return this.imageService.getImageUrl(person?.image, 128, 128);
  }

  openCreateModal(type: string): void {
    this.dialog.openDialog(PartnerActivityFormComponent, {
      data: {
        type
      },
      backdrop: false
    }).subscribe(() => {
      this.fetchActivities();
    });
  }

  openUpdateModal(activity: Activity): void {
    this.dialog.openDialog(PartnerActivityFormComponent, {
      data: {
        activity
      },
      backdrop: false
    }).subscribe(() => {
      this.fetchActivities();
    });
  }

  updateFeedDisplay(activity: Activity): void {
    activity.feedDisplay = !activity.feedDisplay;

    this.updateActivity(activity);
  }

  updatePinned(activity: Activity): void {
    activity.pinned = !activity.pinned;

    this.updateActivity(activity);
  }

  updateActivity(activity: Activity): void {
    if (!activity.id || this.submitting) {
      return;
    }
    this.submitting = true;

    this.activityService.update(activity.id, {
      messageRead: activity.messageRead,
      feedDisplay: activity.feedDisplay,
      pinned: activity.pinned,
      occurringAt: activity.getOccurringAt(),
      status: activity.status
    }).subscribe(() => {
      this.submitting = false;

      this.fetchActivities();

      this.snackbar.success('The task "' + activity.subject + '" has been updated.');
    });
  }

  submitComment(activity: Activity, comment: HTMLTextAreaElement): void {
    this.activityCommentService.create({
      activityId: activity.id,
      senderId: this.connectedUser.id,
      message: comment.value
    }).subscribe(() => {
      this.updateComments(activity).subscribe(() => {
        comment.value = '';

        this.snackbar.success('Comment send successfully.');
      });
    });
  }

  readMessage(activity: Activity): void {
    if (!activity.id || activity.messageRead) {
      return;
    }

    this.activityService.update(activity.id, {messageRead: true}).subscribe(() => {
      activity.messageRead = true;
    });
  }
}
